import React, { useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import JoditEditor from 'jodit-react';
import {
    TextField,
    Button,
    Box,
    Grid,
    FormControl,
    FormLabel,
    Typography,
    IconButton,
    Select,
    InputLabel,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Videocam, Link } from '@mui/icons-material';
import { CloudUpload, Delete } from 'lucide-react';
import { uploadMedia } from '../../../redux/actions/media.actions';
import { useDispatch, useSelector } from 'react-redux';

const BlogForm = ({ onSubmit, FormName, categories = [], setCategoryName, categoryName, isEditMode }) => {
    const editor = useRef(null);
    const dispatch = useDispatch();


    const {
        register,
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors }
    } = useFormContext();

    const { uploadMediaData } = useSelector((state) => {
        return {
            uploadMediaData: state.mediaReducer?.uploadedMedia?.data
        }
    })


    const textEditorContent = watch('text_editor');

    const handleImageUpload = (fieldName) => async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const result = await dispatch(uploadMedia(formData));

             // Assuming your API returns uploaded URL in result.payload.url
            const uploadedUrl = result?.payload?.data;

            if (uploadedUrl) {
                setValue(fieldName, uploadedUrl);
            }
        } catch (err) {
            console.error("Upload failed", err);

        }
    };

    const handleRemoveImage = (fieldName) => () => {
        setValue(fieldName, '');
    };

    const config = {
        readonly: false,
        placeholder: 'Start writing your blog content...',
        buttons: ['bold', 'italic', 'link', 'unlink', 'ul', 'ol', 'image'],
        askBeforePasteHTML: false
    };

    const renderImagePreview = (fieldName, label, errorField) => (
        <FormControl fullWidth>
            <FormLabel>{label}</FormLabel>
            {watch(fieldName) ? (
                <Box sx={{ mt: 1, display: 'inline-block', position: 'relative', width: 'fit-content' }}>
                    <img
                        src={watch(fieldName)}
                        alt={label}
                        style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '10px', display: 'block' }}
                    />
                    <IconButton
                        onClick={handleRemoveImage(fieldName)}
                        size="small"
                        sx={{
                            position: 'absolute', top: -10, right: -10,
                            bgcolor: 'error.main', color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <>
                    <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ mt: 1, width: 'fit-content' }}>
                        Upload {label}
                        <input type="file" hidden accept="image/*" onChange={handleImageUpload(fieldName)} />
                    </Button>
                    {errorField && <Typography color="error" variant="caption">{errorField.message}</Typography>}
                </>
            )}
        </FormControl>
    );

    const handleRemoveFile = (fieldName, relatedField = null) => () => {
        setValue(fieldName, '');
        if (relatedField) setValue(relatedField, '');
    };

    //for video upload
    const renderFilePreview = (fieldName, isVideo = false) => {
        const value = watch(fieldName);
        if (!value) return null;

        return (
            <Box sx={{ mt: 2, position: 'relative' }}>
                {isVideo ? (
                    <video
                        controls
                        src={value}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '150px',
                            borderRadius: '10px',
                            backgroundColor: '#000'
                        }}
                    />
                ) : (
                    <img
                        src={value}
                        alt="Preview"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '150px',
                            borderRadius: '10px'
                        }}
                    />
                )}
                <IconButton
                    onClick={handleRemoveFile(fieldName, isVideo ? 'cover_video' : null)}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'error.dark'
                        }
                    }}
                >
                    <Delete fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    console.log(errors, "categories")
    return (
        <>
            <Typography variant="h5" gutterBottom>
                {FormName}
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
                {FormName === 'Blog' ? (
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Title"
                                {...register('title')}
                                error={!!errors.title}
                                helperText={errors.title?.message}
                            />
                        </Grid>

                        {/* <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Slug"
                                {...register('slug')}
                                error={!!errors.slug}
                                helperText={errors.slug?.message}
                            />
                        </Grid> */}

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Publisher"
                                {...register('publisher')}
                                error={!!errors.publisher}
                                helperText={errors.publisher?.message}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                name="categoryId"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.categoryId}>
                                        <InputLabel id="category-select-label">Choose Category</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="category-select-label"
                                            label="Choose Category"
                                            disabled={isEditMode}
                                        >
                                            <MenuItem value="" disabled>
                                                Select A Category
                                            </MenuItem>
                                            {categories?.map((category) => (
                                                <MenuItem key={category.id} value={category.id} onClick={() => setCategoryName(category.title)}>
                                                    {category.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.categoryId && (
                                            <Typography color="error" variant="caption">
                                                {errors.categoryId.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                name="blog_for"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.blog_for}>
                                        <InputLabel id="blog-for-select-label">Blog</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="blog-for-select-label"
                                            label="Blog"
                                        >
                                            <MenuItem value="" disabled>
                                                Select Blog Type
                                            </MenuItem>
                                            <MenuItem value="com">.com</MenuItem>
                                        </Select>
                                        {errors.blog_for && (
                                            <Typography color="error" variant="caption">
                                                {errors.blog_for.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Redirectional URL"
                                {...register('redirectional_url')}
                                error={!!errors.redirectional_url}
                                helperText={errors.redirectional_url?.message}
                            />
                        </Grid>

                        <Grid item xs={6} md={6}>
                            {renderImagePreview('cover_image', 'Cover Image', errors.cover_image)}
                        </Grid>

                        <Grid item xs={6} md={6}>
                            <FormControl fullWidth>
                                <FormLabel>Cover Video</FormLabel>
                                {watch('cover_video')
                                    ? renderFilePreview('cover_video', true)
                                    : (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                startIcon={<Videocam />}
                                                sx={{ flex: 1 }}
                                            >
                                                {/* Upload File */}
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="video/*"
                                                    onChange={handleImageUpload('cover_video')}
                                                />
                                            </Button>
                                            <TextField
                                                placeholder="Paste video URL"
                                                variant="outlined"
                                                size="small"
                                                sx={{ flex: 1 }}
                                                InputProps={{
                                                    startAdornment: <Link sx={{ mr: 1, color: 'action.active' }} />
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value) {
                                                        setValue('cover_video', e.target.value);
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                            </FormControl>
                        </Grid>

                        {
                            (categoryName.toLowerCase() === "blog" || categoryName.toLowerCase() === "awards") && (
                                <>
                                    <Grid item xs={6} md={6}>
                                        {renderImagePreview('blog_desktop_banner', 'Desktop Banner', errors.blog_desktop_banner)}
                                    </Grid>

                                    <Grid item xs={6} md={6}>
                                        {renderImagePreview('blog_mobile_banner', 'Mobile Banner', errors.blog_mobile_banner)}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <FormLabel>Blog Content</FormLabel>
                                            <Box sx={{ mt: 1, border: '1px solid #ccc', borderRadius: 1 }}>
                                                <JoditEditor
                                                    ref={editor}
                                                    value={textEditorContent}
                                                    config={{ ...config, height: 300, maxHeight: 400 }}
                                                    onBlur={(newContent) => setValue('text_editor', newContent)}
                                                />
                                            </Box>
                                            {errors.text_editor && <Typography color="error" variant="caption">{errors.text_editor.message}</Typography>}
                                        </FormControl>
                                    </Grid>

                                    {/* SEO Section — visible only when category is Blog */}
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 2,
                                            p: 3,
                                            background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)'
                                        }}>
                                            <Divider sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    bgcolor: '#1976d2',
                                                    color: 'white',
                                                    borderRadius: 10,
                                                    fontSize: '0.75rem',
                                                    letterSpacing: 1
                                                }}>
                                                    🔍 SEO DATA
                                                </Typography>
                                            </Divider>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Controller
                                                        name="seo_title"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                fullWidth
                                                                label="SEO Title"
                                                                variant="outlined"
                                                                size="small"
                                                                {...field}
                                                                error={!!errors?.seo_title}
                                                                helperText={errors?.seo_title?.message}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Controller
                                                        name="seo_keywords"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                fullWidth
                                                                label="SEO Keywords"
                                                                variant="outlined"
                                                                size="small"
                                                                {...field}
                                                                error={!!errors?.seo_keywords}
                                                                helperText={errors?.seo_keywords?.message}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Controller
                                                        name="seo_description"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                fullWidth
                                                                label="SEO Description"
                                                                variant="outlined"
                                                                size="small"
                                                                multiline
                                                                minRows={2}
                                                                {...field}
                                                                error={!!errors?.seo_description}
                                                                helperText={errors?.seo_description?.message}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Controller
                                                        name="share_title"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                fullWidth
                                                                label="Share Title"
                                                                variant="outlined"
                                                                size="small"
                                                                {...field}
                                                                error={!!errors?.share_title}
                                                                helperText={errors?.share_title?.message}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Controller
                                                        name="share_description"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                fullWidth
                                                                label="Share Description"
                                                                variant="outlined"
                                                                size="small"
                                                                {...field}
                                                                error={!!errors?.share_description}
                                                                helperText={errors?.share_description?.message}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    {renderImagePreview('share_image', 'Share Image', errors.share_image)}
                                                </Grid>
                                                {/* <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <FormLabel sx={{ mb: 1, fontSize: '0.85rem' }}>Favicon Image</FormLabel>
                                                        {watch('favicon') ? (
                                                            <Box sx={{ mt: 2, position: 'relative' }}>
                                                                <img
                                                                    src={watch('favicon')}
                                                                    alt="Favicon preview"
                                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '10px' }}
                                                                />
                                                                <IconButton onClick={handleRemoveImage('favicon')} sx={{ position: 'absolute', top: -5, left: 180 }}>
                                                                    <DeleteIcon color="error" />
                                                                </IconButton>
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ mt: 1 }}>
                                                                    Upload Favicon Image
                                                                    <input type="file" hidden accept="image/*" onChange={handleImageUpload('favicon')} />
                                                                </Button>
                                                                {errors.favicon && <Typography color="error" variant="caption">{errors.favicon.message}</Typography>}
                                                            </>
                                                        )}
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Controller
                                                        name="seoDone"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <FormControlLabel
                                                                control={<Checkbox {...field} checked={!!field.value} color="primary" />}
                                                                label={<Typography variant="body2">Mark SEO as Done</Typography>}
                                                            />
                                                        )}
                                                    />
                                                </Grid> */}
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </>
                            )
                        }
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Category Title"
                                {...register('title')}
                                error={!!errors.title}
                                helperText={errors.title?.message}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="order"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.order}>
                                        <InputLabel id="order-select-label">Order</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="order-select-label"
                                            label="Order"
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                                <MenuItem key={num} value={num.toString()}>
                                                    {num}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.order && (
                                            <Typography color="error" variant="caption">
                                                {errors.order.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Category Description"
                                {...register('description')}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                            />
                        </Grid>
                    </Grid>
                )}
            </form>
        </>
    );
};

export default BlogForm;